import { useCallback, useContext, useEffect, useState, type ChangeEvent, type MouseEvent } from "react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Table, Tooltip, notification } from "antd";
import clsx from "clsx";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  journalsQueries,
  isSectionFourNoContent,
  TitlePageFillersDropdown,
} from "@/entities/journal";
import {
  JournalSectionContent,
  JournalSectionDescription,
  JournalSectionFilters,
  JournalSectionPageHeader,
  JournalSectionPagination,
  JournalSectionRecordsToolbar,
  JournalSectionWrapper,
  useJournalSectionPagination,
  JournalRecordPreview,
  type JournalRecordPreviewMeta,
  isTablePreviewClick,
} from "@/entities/journal/ui";
import { JournalSectionNavTitle, useJournalSectionPersonnelPageConfig } from "@/features/journal-section-personnel";
import { ModalInfo } from "@/features/modal-info";
import {
  AbilityContext,
  Can,
  EntityBaseAbilityBuilder,
  useUpdateAbility,
} from "@/shared/lib/casl";
import { useCoordinator, useLocalUser, useNavigationPosition } from "@/shared/lib/hooks";
import { useDebounce } from "@/shared/utils";
import { useAppRoutes } from "@shared/lib/react-router";
import { JournalBulkSendToSignModal } from "../../../journal-bulk-send-to-sign";
import { RejectedRecordsCollapsible } from "../../../journals-section-first/ui";
import {
  getSectionFourColumns,
  getSectionFourDataSource,
  type ModalState,
} from "../../model";
import { useSetupSigningString } from "../../model/hooks/setup-signing-string";
import { useAction } from "../../model/hooks/use-action";
import { useCanCreateSectionFourRecord } from "../../model/hooks/use-can-create-section-four-record";
import { useSectionFourController } from "../../model/hooks/use-section-four-controller";
import { useSectionFourSecondPartController } from "../../model/hooks/use-section-four-second-part-controller";
import { useWithSignedString } from "../../model/hooks/with-signed-string";
import { buildSectionFourSignersList } from "../../model/utils/format-section-four-signer-label";
import { JournalVersionModal } from "./journal-version-modal";
import { SectionFourAccessRestricted } from "./ui/section-four-access-restricted";
import { SectionFourAddRecordButton } from "./ui/section-four-add-record-button";
import {
  SectionFourActiveFilterChips,
  SectionFourFilters,
} from "./ui/section-four-filters/section-four-filters";
import "./journals-section-fourth.scss";

export const JournalsSectionFourth = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mainJournalId, titleId, projectId, objectId } = useCoordinator();
  const { isInJournalVersionSection } = useNavigationPosition();
  const appRoutes = useAppRoutes();

  // Если открыта форма или просмотр - рендерим только их, без основного контента
  const isFormOrViewPage = location.pathname.includes("/form/") || location.pathname.includes("/view/");
  const isPersonnelPage = location.pathname.includes("/personnel");
  const { recordsTitle, recordsPath } = useJournalSectionPersonnelPageConfig(4);

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const { data: titleJournal, isLoading: isTitleLoading } = useQuery(
    journalsQueries.getTitleJournal({ titleId }),
  );

  const signersList = buildSectionFourSignersList(
    titleJournal?.mainJournal?.titleJournal?.developer?.constructionControls,
    titleJournal?.mainJournal?.titleJournal?.technicalCustomer
      ?.constructionControls,
  );

  const {
    isAddDisabled,
    addButtonTooltip,
    canOpenCreateForm,
  } = useCanCreateSectionFourRecord(
    titleJournal,
    isTitleLoading,
    signersList.length,
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [search, setSearch] = useState("");
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [controlPeriodStart, setControlPeriodStart] = useState<string>("");
  const [controlPeriodEnd, setControlPeriodEnd] = useState<string>("");
  const [defectDeadline, setDefectDeadline] = useState<string>("");
  const [eliminationDate, setEliminationDate] = useState<string>("");
  const [createDate, setCreateDate] = useState<string>("");
  const [updateDate, setUpdateDate] = useState<string>("");
  const [defectsFilter, setDefectsFilter] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const itemNoVersionId = searchParams.get("itemNoVersionId");
  const action = searchParams.get("action");

  const [modalState, setModalState] = useState<ModalState>({
    visible: false,
    title: "",
    info: "",
    confirmText: "",
    cancelText: "",
    inputPlaceholder: "",
    inputValue: "",
    isInputModal: false,
    action: itemNoVersionId ? action || "VIEW" : null,
    signerEmployeeId: null,
    sectionTwoAction: null,
  });
  const [inputError, setInputError] = useState("");


  const [isBulkSendModalOpen, setIsBulkSendModalOpen] = useState(false);
  const [previewRecord, setPreviewRecord] = useState<JournalRecordPreviewMeta | null>(
    null,
  );

  const clearSelection = () => {};

  const pagination = useJournalSectionPagination(10);
  const { page: currentPage, setPage: setCurrentPage, limit: pageSize } = pagination;
  const [signerIds, setSignerIds] = useState<string[]>([]);

  const handleFiltersApply = () => {
    setCurrentPage(1);
  };

  const [versionModalItemId, setVersionModalItemId] = useState<string | null>(
    null,
  );

  const localUser = useLocalUser();

  const debouncedSearch = useDebounce(search, 300);

  const { data: sectionFourData, isFetching, isSuccess } = useQuery({
    ...journalsQueries.getSection4({
      section4ListId:
        mainJournalData?.mainJournal?.section4?.sectionListId || "",
      page: currentPage,
      limit: pageSize,
      search: debouncedSearch,
      status: selectedStatuses.join(",") || undefined,
      signerId: signerIds.join(",") || undefined,
      dateFrom: controlPeriodStart || undefined,
      dateTo: controlPeriodEnd || undefined,
      defectFixingPlanDate: defectDeadline || undefined,
      defectFixingFactDate: eliminationDate || undefined,
      createDate: createDate || undefined,
      updateDate: updateDate || undefined,
      isDefects: defectsFilter || undefined,
    }),
    enabled: !!mainJournalData?.mainJournal?.section4?.sectionListId,
  });

  const isAccessRestricted =
    (isSuccess && sectionFourData == null) ||
    isSectionFourNoContent(sectionFourData);

  const section4List = isSectionFourNoContent(sectionFourData)
    ? []
    : sectionFourData?.section4List || [];

  const rejectedWithComment = (section4List || []).filter(
    (item: any) => {
      // В разделе 4 статус REJECTED может быть у itemPartOne или itemPartTwo
      const isPartOneRejected = item?.itemPartOne?.status === "REJECTED";
      const isPartTwoRejected = item?.itemPartTwo?.status === "REJECTED";
      
      if (!isPartOneRejected && !isPartTwoRejected) return false;
      
      // Поддержка массива comments (для раздела 4)
      if (item?.comments && Array.isArray(item.comments) && item.comments.length > 0) {
        // Фильтруем комментарии для rejected частей
        const itemPartOneId = item?.itemPartOne?.id;
        const itemPartTwoId = item?.itemPartTwo?.id;
        
        const validComments = item.comments.filter(
          (comment: any) => {
            const commentText = comment?.commentText ?? comment?.comment;
            if (!commentText || !commentText.toString().trim()) return false;
            
            // Проверяем, относится ли комментарий к rejected части
            const commentItemPart = comment?.itemPart;
            return (
              (isPartOneRejected && commentItemPart === itemPartOneId) ||
              (isPartTwoRejected && commentItemPart === itemPartTwoId)
            );
          },
        );
        return validComments.length > 0;
      }
      // Поддержка одиночного comment (для обратной совместимости)
      return (item?.comment ?? "").toString().trim();
    },
  );

  useEffect(() => {
    const id = searchParams.get("itemNoVersionId");
    const act = searchParams.get("action");
    if (id && act === "EDIT") {
      navigate(
        appRoutes.journals.section4Form(projectId, objectId, mainJournalId, titleId, id),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      setSearchParams(params);
    } else if (id && act === "VIEW") {
      navigate(
        appRoutes.journals.section4View(projectId, objectId, mainJournalId, titleId, id),
      );
      const params = new URLSearchParams(searchParams);
      params.delete("action");
      setSearchParams(params);
    }
  // eslint-disable-next-line react-compiler/react-compiler
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate, projectId, objectId, mainJournalId, titleId, setSearchParams]);
  useUpdateAbility({
    // @ts-expect-error я тебя ненавижу
    permissions: isSectionFourNoContent(sectionFourData)
      ? undefined
      : sectionFourData?.permissions,
    abilityBuilder: EntityBaseAbilityBuilder(),
  });
  const ability = useContext(AbilityContext);

  const {
    handleDelete,
    handleSendToSign,
    handleReject,
    handleReturn,
    handleSendToSignBatch,
    handleStatusReturn,
  } = useSectionFourController(
    mainJournalData?.mainJournal?.section4?.sectionListId || "",
    mainJournalData?.mainJournal?.section4?.sectionId || "",
  );

  const {
    handleSendToSign: handleSendToSignTwo,
    handleReject: handleRejectTwo,
    handleReturn: handleReturnTwo,
    handleStatusReturn: handleStatusReturnTwo,
  } = useSectionFourSecondPartController(
    mainJournalData?.mainJournal?.section4?.sectionListId || "",
    mainJournalData?.mainJournal?.section4?.sectionId || "",
  );

  const { handleAction, handleOpenForm } = useAction({
    handleDelete,
    modalState,
    setModalState,
    handleSendToSign,
    handleReject,
    handleReturn,
    handleSendToSignBatch,
    setSelectedRowKeys: clearSelection,
    setIsColumnSelectorVisible: () => {},
    handleStatusReturn,
    handleSendToSignTwo,
    handleRejectTwo,
    handleReturnTwo,
    handleStatusReturnTwo,
  });

  const notifySectionFourAccessRestricted = () => {
    notification.warning({
      message: "Доступ ограничен",
      description:
        "Добавьте свою организацию в титульный лист, чтобы добавлять записи в раздел 4.",
    });
  };

  const openSectionFourForm: typeof handleOpenForm = (params) => {
    const isCreate =
      params.formType === "ADD" ||
      !params.itemNoVersionId ||
      params.itemNoVersionId === "new";

    if (isAccessRestricted && isCreate) {
      notifySectionFourAccessRestricted();
      return;
    }

    if (isCreate && !canOpenCreateForm) {
      return;
    }

    handleOpenForm(params);
  };

  const handleModalCancel = () => {
    setModalState((prev) => ({
      ...prev,
      visible: false,
      inputValue: "",
      action: null,
      signerEmployeeId: null,
      actionPayload: null,
      sectionTwoAction: null,
    }));
    setInputError("");
  };

  const onChangeValue = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setModalState((prev) => ({
      ...prev,
      inputValue: e.target.value,
    }));
    if (e.target.value.trim()) {
      setInputError("");
    }
  };

  const handleStartBulkSign = () => {
    setIsBulkSendModalOpen(true);
  };

  const sectionFourId =
    mainJournalData?.mainJournal?.section4?.sectionListId ||
    mainJournalData?.mainJournal?.section4?.sectionId;

  const dataSource = getSectionFourDataSource(
    // @ts-expect-error section4List отсутствует в openapi типе ответа раздела 4
    section4List,
  );

  const {
    signingString,
    setupSigningStringPartOne,
    setupSigningStringPartTwo,
    isPending: isSigningPending,
  } = useSetupSigningString(mainJournalData, debouncedSearch);
  const { withSignedStringPartOne, withSignedStringPartTwo } =
    useWithSignedString();

  const columns = getSectionFourColumns({
    setModalState,
    handleOpenForm: openSectionFourForm,
    navigate,
    projectId,
    objectId,
    mainJournalId,
    titleId,
    localUser,
    withSignedStringPartOne,
    setupSigningStringPartOne,
    signingString,
    mainJournalData,
    isSigningPending,
    withSignedStringPartTwo,
    setupSigningStringPartTwo,
    setSearchParams,
    setVersionModalItemId,
    appRoutes,
  });

  const resolveBulkSendTable = useCallback(
    (data: unknown) => {
      const list = isSectionFourNoContent(data)
        ? []
        : (data as { section4List?: Parameters<typeof getSectionFourDataSource>[0] })
            ?.section4List ?? [];

      return {
        rowKey: "key",
        tableClassName: "table-fourth",
        dataSource: getSectionFourDataSource(list),
        columns: getSectionFourColumns({
          bulkSendMode: true,
          setModalState,
          handleOpenForm: openSectionFourForm,
          navigate,
          projectId,
          objectId,
          mainJournalId,
          titleId,
          localUser,
          withSignedStringPartOne,
          setupSigningStringPartOne,
          signingString,
          mainJournalData,
          isSigningPending,
          withSignedStringPartTwo,
          setupSigningStringPartTwo,
          setSearchParams,
          setVersionModalItemId,
          appRoutes,
        }),
      };
    },
    [
      openSectionFourForm,
      navigate,
      projectId,
      objectId,
      mainJournalId,
      titleId,
      localUser,
      withSignedStringPartOne,
      setupSigningStringPartOne,
      signingString,
      mainJournalData,
      isSigningPending,
      withSignedStringPartTwo,
      setupSigningStringPartTwo,
      setSearchParams,
      appRoutes,
    ],
  );

  useEffect(() => {
    if (action === "VERSION" && itemNoVersionId) {
      setVersionModalItemId(itemNoVersionId);
    } else if (action === "VIEW" && itemNoVersionId) {
      // оставляем itemNoVersionId, закрываем модалку версий
      setVersionModalItemId(null);
    } else {
      setVersionModalItemId(null);
    }
  }, [action, itemNoVersionId]);
  return (
    <>
      {isFormOrViewPage || isPersonnelPage ? (
        <Outlet />
      ) : (
        <>
      <JournalSectionWrapper>
        {!isInJournalVersionSection && !isTitleLoading ? (
          <JournalSectionPageHeader
            banner={
              !isAccessRestricted && signersList.length === 0 ? (
                <>
                  Для добавления записи в Раздел 4 не хватает информации в
                  Титульном листе в блоке{" "}
                  <span className="journal-section-page-header__hint-link">
                    Уполномоченный представитель застройщика, или
                    технического заказчика, лица, ответственного за
                    эксплуатацию здания, сооружения, или регионального
                    оператора по вопросам строительного контроля
                  </span>
                  .{" "}
                  <TitlePageFillersDropdown
                    sectionType="sectionFour"
                    sectionId={sectionFourId}
                  />
                </>
              ) : undefined
            }
            title={
              <JournalSectionNavTitle
                recordsTitle={recordsTitle}
                recordsPath={recordsPath}
                activeTab="records"
              />
            }
            actions={
              isAccessRestricted ? undefined : (
                <Can I="edit" a="section_4">
                  <JournalSectionRecordsToolbar
                    onStartBulkSign={handleStartBulkSign}
                    showBulkActions={!isAddDisabled}
                    addButton={
                      <SectionFourAddRecordButton
                        disabled={isAddDisabled}
                        tooltip={addButtonTooltip}
                        onClick={() =>
                          openSectionFourForm({
                            formType: "ADD",
                          })
                        }
                      />
                    }
                  />
                </Can>
              )
            }
          />
        ) : (
          <JournalSectionNavTitle
            recordsTitle={recordsTitle}
            recordsPath={recordsPath}
            activeTab="records"
          />
        )}

        <JournalSectionDescription>
          <span className="journals-section-fourth-description">
            Сведения о строительном контроле в процессе строительства,
            реконструкции, капитального ремонта объекта капитального
            строительства
            <Tooltip
              title={
                <>
                  Раздел доступен сотрудникам организаций: застройщик,
                  эксплуатирующая организации, региональный оператор,
                  технический заказчик, лицо осуществляющее подготовку проектной
                  документации, авторский надзор, лицо осуществляющее
                  строительство.
                  <br />
                  <br />
                  Сотрудники иных организаций имеют доступ в раздел без
                  просмотра созданных записей.
                </>
              }
            >
              <InfoCircleOutlined className="journals-section-fourth-description__info" />
            </Tooltip>
          </span>
        </JournalSectionDescription>

        <JournalSectionFilters>
          <SectionFourFilters
            onSearchChange={setSearch}
            selectedStatuses={selectedStatuses}
            setSelectedStatuses={setSelectedStatuses}
            signerIds={signerIds}
            setSignerIds={setSignerIds}
            controlPeriodStart={controlPeriodStart}
            controlPeriodEnd={controlPeriodEnd}
            setControlPeriodStart={setControlPeriodStart}
            setControlPeriodEnd={setControlPeriodEnd}
            defectDeadline={defectDeadline}
            setDefectDeadline={setDefectDeadline}
            eliminationDate={eliminationDate}
            setEliminationDate={setEliminationDate}
            createDate={createDate}
            setCreateDate={setCreateDate}
            updateDate={updateDate}
            setUpdateDate={setUpdateDate}
            defectsFilter={defectsFilter}
            setDefectsFilter={setDefectsFilter}
            signersList={signersList}
            onFiltersApply={handleFiltersApply}
          />
        </JournalSectionFilters>

        <JournalSectionContent>
          {isAccessRestricted ? (
            <SectionFourAccessRestricted />
          ) : (
            <>
          {rejectedWithComment.length > 0 ? (
            <div className="journal-info-block--spaced-bottom">
              <RejectedRecordsCollapsible
                items={rejectedWithComment as any}
                editSubject="section_4"
                onReturnToWork={(item: any) => {
                  const itemNoVersionId = item?.itemNoVersionId;
                  const itemPartOneId = item?.itemPartOne?.id;
                  const itemPartTwoId = item?.itemPartTwo?.id;

                  if (itemNoVersionId) {
                    if (itemPartTwoId && !itemPartOneId) {
                      setSearchParams({
                        itemNoVersionId,
                        itemPartTwo: itemPartTwoId,
                      });
                    } else {
                      setSearchParams({
                        itemNoVersionId,
                        itemPartOne: itemPartOneId,
                      });
                    }
                  }

                  setModalState((prev) => ({
                    ...prev,
                    visible: true,
                    title: "Вернуть запись в работу",
                    info: "Вы действительно хотите вернуть запись в статус Новый?",
                    confirmText: "Вернуть",
                    cancelText: "Отмена",
                    inputPlaceholder: "Комментарий",
                    inputValue: "",
                    isInputModal: false,
                    action: "RETURN",
                    signerEmployeeId: prev.signerEmployeeId || null,
                  }));
                }}
              />
            </div>
          ) : null}
          <AbilityContext.Provider value={ability}>
            <SectionFourActiveFilterChips
              selectedStatuses={selectedStatuses}
              setSelectedStatuses={setSelectedStatuses}
              signerIds={signerIds}
              setSignerIds={setSignerIds}
              defectDeadline={defectDeadline}
              setDefectDeadline={setDefectDeadline}
              eliminationDate={eliminationDate}
              setEliminationDate={setEliminationDate}
              signersList={signersList}
              onFilterRemove={handleFiltersApply}
            />
            <Table
              loading={isFetching}
              className="table-fourth"
              rowClassName={(record: any) =>
                clsx(
                  "journals-section-fourth-row",
                  (record?.itemInfo?.itemPartOne?.status === "REJECTED" ||
                    record?.itemInfo?.itemPartTwo?.status === "REJECTED") &&
                    "section-row--rejected",
                  previewRecord?.itemNoVersionId === record?.key &&
                    "journal-section-row--preview-selected",
                )
              }
              onRow={(record: any) => ({
                onClick: (event: MouseEvent<HTMLElement>) => {
                  if (!isTablePreviewClick(event)) return;
                  setPreviewRecord({
                    itemNoVersionId: record.key,
                    sequenceNumber: record?.itemInfo?.sequenceNumber,
                    status: record?.itemInfo?.itemPartOne?.status,
                    isCurrent: record?.itemInfo?.isCurrent,
                  });
                },
              })}
              columns={columns}
              dataSource={dataSource}
              pagination={false}
            />
            <JournalSectionPagination
              pagination={pagination}
              total={
                isSectionFourNoContent(sectionFourData)
                  ? 0
                  : sectionFourData?.total || 0
              }
            />
          </AbilityContext.Provider>
            </>
          )}
        </JournalSectionContent>

        <JournalBulkSendToSignModal
          open={isBulkSendModalOpen}
          onClose={() => setIsBulkSendModalOpen(false)}
          section={4}
          sectionFourListId={
            mainJournalData?.mainJournal?.section4?.sectionListId
          }
          signersList={signersList}
          resolveTable={resolveBulkSendTable}
          onSendBatch={async (items) => {
            await handleSendToSignBatch(items);
            setIsBulkSendModalOpen(false);
          }}
        />

        <ModalInfo
          visible={modalState.visible}
          handleAction={(a) => {
            if (modalState.isInputModal && !modalState.inputValue?.trim()) {
              setInputError("Укажите причину отклонения");
              return;
            }
            const actionString = typeof a === 'object' && a !== null && 'type' in a && typeof a.type === 'string'
              ? a.type 
              : (modalState.action || (typeof a === 'string' ? a : null));
            
            if (actionString) {
              handleAction(actionString);
            }
          }}
          modalState={modalState as any}
          onCancel={handleModalCancel}
          title={modalState.title}
          Info={modalState.info}
          confirmText={modalState.confirmText}
          cancelText={modalState.cancelText}
          inputPlaceholder={modalState.inputPlaceholder}
          onChangeValue={onChangeValue}
          inputValue={modalState.inputValue}
          isInputModal={modalState.isInputModal}
          isDivider={modalState.isDivider}
          inputError={inputError}
          onValidationError={() => setInputError("Укажите причину отклонения")}
        />
      </JournalSectionWrapper>
      {versionModalItemId && (
        <JournalVersionModal
          open={!!versionModalItemId}
          onCancel={() => {
            setVersionModalItemId(null);
            const params = new URLSearchParams(searchParams);
            if (params.get("action") === "VERSION") {
              params.delete("action");
              params.delete("itemNoVersionId");
            }
            setSearchParams(params);
          }}
          sectionFourListId={
            mainJournalData?.mainJournal?.section4?.sectionListId || ""
          }
          itemNoVersionId={versionModalItemId}
        />
        )}
        <JournalRecordPreview
          sectionNumber={4}
          record={previewRecord}
          onClose={() => setPreviewRecord(null)}
          onOpenRecord={(itemNoVersionId) => {
            navigate(
              appRoutes.journals.section4View(
                projectId,
                objectId,
                mainJournalId,
                titleId,
                itemNoVersionId,
              ),
            );
            setPreviewRecord(null);
          }}
        />
        </>
      )}
    </>
  );
};
